import { useState, useMemo, useRef, useEffect } from 'react';
import { useQueries } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Trophy, Clock, Minus, Loader2, AlertTriangle, Search, X, UnfoldVertical, FoldVertical, ChevronsDown, Swords } from 'lucide-react';
import { apiService, queryKeys } from '@/services/api';
import type { ScheduleMatchup, ScorecardMatch, DivisionInfo, DivisionPlayer, SessionScorecardResponse } from '@/types/api';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import HeadToHeadModal from '@/components/HeadToHeadModal';
import TeamDetailModal from '@/components/TeamDetailModal';

interface ScheduleViewProps {
  divisionIds: string[];
  divisions: DivisionInfo[];
  players?: DivisionPlayer[];
}

// Combined matchup — one per team pairing per week, with summed scores across divisions
interface CombinedMatchup {
  week: number;
  date: string;
  home_team: string;
  away_team: string;
  score_home: number | null;
  score_away: number | null;
  is_bye: boolean;
  is_played: boolean;
  // Per-division data for scorecards
  divisionMatchups: { divisionId: string; matchup: ScheduleMatchup }[];
}

interface WeekGroup {
  week: number;
  date: string;
  matchups: CombinedMatchup[];
}

const EXPECTED_MATCHES_PER_DIVISION = 3;

interface TeamStanding {
  team: string;
  wins: number;
  losses: number;
  ties: number;
  pointsFor: number;
  pointsAgainst: number;
}

/** Short label from a division name (e.g. "8-Ball", "Lagger's Choice") */
function divisionLabel(name: string | null | undefined): string {
  if (!name) return 'Division';
  const lower = name.toLowerCase();
  if (lower.includes('lagger')) return "Lagger's Choice";
  if (lower.includes('8') || lower.includes('eight')) return '8-Ball';
  if (lower.includes('9') || lower.includes('nine')) return '9-Ball';
  if (lower.includes('10') || lower.includes('ten')) return '10-Ball';
  return name;
}

function formatWeekDate(dateStr: string): string {
  return dateStr || '';
}

// ─── Individual Match Row ───────────────────────────────────────────

function MatchRow({ m, highlighted }: { m: ScorecardMatch; highlighted?: boolean }) {
  const [h2hOpen, setH2hOpen] = useState(false);
  const homeWon = m.winner === 'home';
  const awayWon = m.winner === 'away';
  const rowBg = highlighted ? 'bg-brand-50/60' : '';

  const h2hButton = (
    <button
      onClick={(e) => { e.stopPropagation(); setH2hOpen(true); }}
      title="Head-to-head history"
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors shrink-0 border border-amber-200/60"
    >
      <Swords className="w-3 h-3" />
      <span className="hidden sm:inline">H2H</span>
    </button>
  );

  return (
    <div className={`px-3 py-2.5 border-b border-slate-100 last:border-b-0 ${rowBg}`}>
      {/* Desktop layout */}
      <div className="hidden md:flex items-center gap-3 text-sm">
        <span className={`w-5 h-5 shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${homeWon ? 'bg-emerald-500' : awayWon ? 'bg-red-400' : 'bg-slate-300'}`}>
          {homeWon ? 'W' : awayWon ? 'L' : '–'}
        </span>
        <span className={`font-medium min-w-[140px] truncate ${homeWon ? 'text-slate-900' : 'text-slate-500'}`}>
          {m.home_player.name}
        </span>
        {m.home_player.csr != null && (
          <span className="text-xs text-slate-400 tabular-nums w-8 text-right">{m.home_player.csr}</span>
        )}
        <div className="flex items-center gap-1.5 shrink-0 mx-1">
          {m.home_race != null && m.away_race != null && (
            <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
              Race {m.home_race}–{m.away_race}
            </span>
          )}
          {m.home_games_won != null && m.away_games_won != null && (
            <span className="text-xs font-semibold tabular-nums bg-slate-100 px-1.5 py-0.5 rounded">
              <span className={homeWon ? 'text-emerald-600' : 'text-slate-400'}>{m.home_games_won}</span>
              <span className="text-slate-300 mx-0.5">–</span>
              <span className={awayWon ? 'text-emerald-600' : 'text-slate-400'}>{m.away_games_won}</span>
            </span>
          )}
          {m.home_score != null && m.away_score != null && (
            <span className="text-xs tabular-nums bg-amber-50 text-amber-800 px-1.5 py-0.5 rounded">
              (pts: <span className={homeWon ? 'font-semibold' : 'font-normal'}>{m.home_score}</span>
              <span className="text-amber-300 mx-0.5">–</span>
              <span className={awayWon ? 'font-semibold' : 'font-normal'}>{m.away_score}</span>)
            </span>
          )}
        </div>
        <span className={`w-5 h-5 shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${awayWon ? 'bg-emerald-500' : homeWon ? 'bg-red-400' : 'bg-slate-300'}`}>
          {awayWon ? 'W' : homeWon ? 'L' : '–'}
        </span>
        <span className={`font-medium min-w-[140px] truncate ${awayWon ? 'text-slate-900' : 'text-slate-500'}`}>
          {m.away_player.name}
        </span>
        {m.away_player.csr != null && (
          <span className="text-xs text-slate-400 tabular-nums w-8 text-right">{m.away_player.csr}</span>
        )}
        <div className="flex items-center gap-1.5 ml-auto shrink-0">
          {h2hButton}
          {m.game_type && (
            <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{m.game_type}</span>
          )}
        </div>
      </div>

      {/* Mobile layout */}
      <div className="flex md:hidden flex-col gap-1 text-sm">
        <div className="flex items-center gap-2">
          <span className={`w-5 h-5 shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${homeWon ? 'bg-emerald-500' : awayWon ? 'bg-red-400' : 'bg-slate-300'}`}>
            {homeWon ? 'W' : awayWon ? 'L' : '–'}
          </span>
          <span className={`font-medium truncate flex-1 ${homeWon ? 'text-slate-900' : 'text-slate-500'}`}>
            {m.home_player.name}
            {m.home_player.csr != null && <span className="text-xs text-slate-400 ml-1">({m.home_player.csr})</span>}
          </span>
          {m.home_games_won != null && m.away_games_won != null && (
            <span className="text-xs font-bold tabular-nums shrink-0">
              <span className={homeWon ? 'text-emerald-600' : 'text-slate-400'}>{m.home_games_won}</span>
              <span className="text-slate-300 mx-0.5">–</span>
              <span className={awayWon ? 'text-emerald-600' : 'text-slate-400'}>{m.away_games_won}</span>
            </span>
          )}
          {m.home_score != null && m.away_score != null && (
            <span className="text-[10px] tabular-nums text-amber-700 bg-amber-50 px-1 py-0.5 rounded shrink-0">
              (pts: {m.home_score}–{m.away_score})
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 pl-0">
          <span className={`w-5 h-5 shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${awayWon ? 'bg-emerald-500' : homeWon ? 'bg-red-400' : 'bg-slate-300'}`}>
            {awayWon ? 'W' : homeWon ? 'L' : '–'}
          </span>
          <span className={`truncate flex-1 ${awayWon ? 'text-slate-900' : 'text-slate-500'}`}>
            {m.away_player.name}
            {m.away_player.csr != null && <span className="text-xs text-slate-400 ml-1">({m.away_player.csr})</span>}
          </span>
          {h2hButton}
          {m.home_race != null && m.away_race != null && (
            <span className="text-[10px] text-slate-400 shrink-0">Race {m.home_race}–{m.away_race}</span>
          )}
        </div>
      </div>

      {h2hOpen && (
        <HeadToHeadModal
          isOpen={h2hOpen}
          onClose={() => setH2hOpen(false)}
          playerAId={m.home_player.player_id}
          playerAName={m.home_player.name}
          playerBId={m.away_player.player_id}
          playerBName={m.away_player.name}
          initialGameType={m.game_type as '8-ball' | '9-ball' | '10-ball' | undefined}
        />
      )}
    </div>
  );
}

// ─── Missing Match Placeholder Row ──────────────────────────────────

function MissingMatchRow({ homePoints, awayPoints }: { homePoints: number; awayPoints: number }) {
  const homeWon = homePoints > awayPoints;
  const awayWon = awayPoints > homePoints;

  return (
    <div className="px-3 py-2.5 border-b border-slate-100 last:border-b-0 bg-slate-50/50">
      {/* Desktop layout */}
      <div className="hidden md:flex items-center gap-3 text-sm">
        <span className="w-5 h-5 shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold text-white bg-slate-300">
          ?
        </span>
        <span className="text-slate-400 italic min-w-[140px]">Match data unavailable</span>
        <div className="flex items-center gap-1.5 shrink-0 mx-1">
          <span className="text-xs tabular-nums bg-amber-50 text-amber-800 px-1.5 py-0.5 rounded">
            (pts: <span className={homeWon ? 'font-semibold' : 'font-normal'}>{homePoints}</span>
            <span className="text-amber-300 mx-0.5">–</span>
            <span className={awayWon ? 'font-semibold' : 'font-normal'}>{awayPoints}</span>)
          </span>
        </div>
        <span className="text-[10px] text-slate-400 bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded ml-auto">makeup</span>
      </div>

      {/* Mobile layout */}
      <div className="flex md:hidden items-center gap-2 text-sm">
        <span className="w-5 h-5 shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold text-white bg-slate-300">
          ?
        </span>
        <span className="text-slate-400 italic truncate flex-1">Match data unavailable</span>
        <span className="text-[10px] tabular-nums text-amber-700 bg-amber-50 px-1 py-0.5 rounded shrink-0">
          (pts: {homePoints}–{awayPoints})
        </span>
      </div>
    </div>
  );
}

// ─── Division Match List (handles filter expand/collapse) ───────────

function DivisionMatchList({ matches, highlightPlayerIds, missingMatches }: {
  matches: ScorecardMatch[];
  highlightPlayerIds?: Set<string>;
  missingMatches: { homePoints: number; awayPoints: number }[];
}) {
  const hasFilter = highlightPlayerIds && highlightPlayerIds.size > 0;
  const [showHidden, setShowHidden] = useState(false);

  const visible = hasFilter && !showHidden
    ? matches.filter((m: ScorecardMatch) =>
        highlightPlayerIds.has(m.home_player.player_id) || highlightPlayerIds.has(m.away_player.player_id))
    : matches;
  const hiddenCount = matches.length - (hasFilter
    ? matches.filter((m: ScorecardMatch) =>
        highlightPlayerIds.has(m.home_player.player_id) || highlightPlayerIds.has(m.away_player.player_id)).length
    : 0);

  return (
    <div>
      {visible.map((m: ScorecardMatch, i: number) => (
        <MatchRow key={i} m={m} highlighted={hasFilter && !showHidden} />
      ))}
      {hasFilter && hiddenCount > 0 && (
        <button
          onClick={() => setShowHidden(!showHidden)}
          className="w-full px-3 py-1.5 border-b border-slate-100 last:border-b-0 text-xs text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors text-center cursor-pointer"
        >
          {showHidden ? '⋯ hide other matches' : `⋯ ${hiddenCount} other ${hiddenCount === 1 ? 'match' : 'matches'}`}
        </button>
      )}
      {missingMatches.map((mm, i) => (
        <MissingMatchRow key={`missing-${i}`} homePoints={mm.homePoints} awayPoints={mm.awayPoints} />
      ))}
    </div>
  );
}

// ─── Division Section within Scorecard ──────────────────────────────

function DivisionSection({ label, data, isLoading, error, highlightPlayerIds }: {
  label: string;
  data: SessionScorecardResponse | undefined;
  isLoading: boolean;
  error: Error | null;
  highlightPlayerIds?: Set<string>;
}) {
  const matchCount = data?.matches?.length ?? 0;
  const needsMakeup = data && matchCount > 0 && matchCount < EXPECTED_MATCHES_PER_DIVISION;

  // Compute point gap for missing matches
  const missingMatches = useMemo(() => {
    if (!needsMakeup || !data || data.score_home == null || data.score_away == null) return [];

    const sumHome = data.matches.reduce((s, m) => s + (m.home_score ?? 0), 0);
    const sumAway = data.matches.reduce((s, m) => s + (m.away_score ?? 0), 0);
    const gapHome = data.score_home - sumHome;
    const gapAway = data.score_away - sumAway;
    const missingCount = EXPECTED_MATCHES_PER_DIVISION - matchCount;

    if (gapHome === 0 && gapAway === 0) return [];

    // If only 1 missing match, show exact inferred points
    if (missingCount === 1) {
      return [{ homePoints: gapHome, awayPoints: gapAway }];
    }

    // Multiple missing: show total gap as single placeholder
    return [{ homePoints: gapHome, awayPoints: gapAway }];
  }, [needsMakeup, data, matchCount]);

  return (
    <div>
      {/* Division header */}
      <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100/80 border-b border-slate-200">
        <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">{label}</span>
        {data && data.score_home != null && data.score_away != null && (
          <span className="text-xs tabular-nums text-slate-500 ml-auto">
            <span className={data.score_home > data.score_away ? 'font-semibold text-slate-700' : ''}>{data.score_home}</span>
            <span className="text-slate-300 mx-1">–</span>
            <span className={data.score_away > data.score_home ? 'font-semibold text-slate-700' : ''}>{data.score_away}</span>
          </span>
        )}
        {needsMakeup && (
          <span className={`flex items-center gap-1 text-[10px] font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full ${data?.score_home != null ? '' : 'ml-auto'}`}>
            <AlertTriangle className="w-3 h-3" />
            Makeup
          </span>
        )}
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-3">
          <Loader2 className="w-3.5 h-3.5 text-slate-400 animate-spin" />
          <span className="ml-2 text-xs text-slate-400">Loading...</span>
        </div>
      )}

      {!isLoading && (error || !data) && (
        <div className="px-3 py-2 text-xs text-slate-400">No data available.</div>
      )}

      {!isLoading && data && matchCount === 0 && (
        <div className="px-3 py-2 text-xs text-slate-400">No individual matches recorded.</div>
      )}

      {!isLoading && data && matchCount > 0 && (
        <DivisionMatchList
          matches={data.matches}
          highlightPlayerIds={highlightPlayerIds}
          missingMatches={missingMatches}
        />
      )}
    </div>
  );
}

// ─── Multi-Division Scorecard Detail ────────────────────────────────

function ScorecardDetail({ combinedMatchup, divisionNames, highlightPlayerIds }: {
  combinedMatchup: CombinedMatchup;
  divisionNames: Record<string, string>;
  highlightPlayerIds?: Set<string>;
}) {
  const { week, home_team: homeTeam, away_team: awayTeam, divisionMatchups } = combinedMatchup;

  const results = useQueries({
    queries: divisionMatchups.map(dm => ({
      queryKey: [...queryKeys.sessionScorecard(dm.divisionId, week), homeTeam, awayTeam],
      queryFn: () => apiService.getSessionScorecard(dm.divisionId, week, homeTeam, awayTeam),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const allLoading = results.every(r => r.isLoading);
  const allEmpty = results.every(r => !r.isLoading && (!r.data || r.data.matches.length === 0));

  if (allLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
        <span className="ml-2 text-xs text-slate-400">Loading scorecard...</span>
      </div>
    );
  }

  if (allEmpty) {
    return (
      <div className="px-3 py-3 text-xs text-slate-400">
        No individual match data available for this session.
      </div>
    );
  }

  return (
    <div className="border-t border-slate-200 bg-slate-50/50">
      {divisionMatchups.map((dm, idx) => (
        <DivisionSection
          key={dm.divisionId}
          label={divisionNames[dm.divisionId] || `Division ${idx + 1}`}
          data={results[idx].data}
          isLoading={results[idx].isLoading}
          error={results[idx].error as Error | null}
          highlightPlayerIds={highlightPlayerIds}
        />
      ))}
    </div>
  );
}

// ─── Matchup Row (team score line) ──────────────────────────────────

function MatchupRow({ matchup, divisionNames, expandOverride, highlightPlayerIds }: {
  matchup: CombinedMatchup;
  divisionNames: Record<string, string>;
  expandOverride?: boolean;
  highlightPlayerIds?: Set<string>;
}) {
  const [expanded, setExpanded] = useState(expandOverride ?? false);
  const { home_team, away_team, score_home, score_away, is_bye, is_played } = matchup;

  // Sync local state when controlled expansion changes
  useEffect(() => {
    setExpanded(expandOverride ?? false);
  }, [expandOverride]);

  // Prefetch scorecard data to detect makeups (fires when week is expanded = component mounts)
  const scorecardResults = useQueries({
    queries: is_played && !is_bye
      ? matchup.divisionMatchups.map(dm => ({
          queryKey: [...queryKeys.sessionScorecard(dm.divisionId, matchup.week), home_team, away_team],
          queryFn: () => apiService.getSessionScorecard(dm.divisionId, matchup.week, home_team, away_team),
          staleTime: 5 * 60 * 1000,
        }))
      : [],
  });

  const needsMakeup = scorecardResults.some(r =>
    r.data && r.data.matches.length > 0 && r.data.matches.length < EXPECTED_MATCHES_PER_DIVISION
  );

  if (is_bye) {
    return (
      <div className="flex items-center gap-2 px-3 py-2.5 text-sm text-slate-400 italic">
        <Minus className="w-3.5 h-3.5" />
        <span>{home_team || away_team}</span>
        <span className="text-xs bg-slate-100 text-slate-400 px-2 py-0.5 rounded-full">BYE</span>
      </div>
    );
  }

  if (is_played && score_home != null && score_away != null) {
    const homeWon = score_home > score_away;
    const awayWon = score_away > score_home;
    const tied = score_home === score_away;

    return (
      <div className="rounded-lg overflow-hidden">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center gap-2 px-3 py-2.5 md:py-2 rounded-lg hover:bg-slate-50 active:bg-slate-100 transition-colors cursor-pointer text-left"
        >
          {/* Desktop layout */}
          <div className="hidden md:flex items-center gap-2 flex-1 min-w-0">
            <span className={`text-sm font-medium truncate ${homeWon ? 'text-slate-900' : 'text-slate-500'}`}>
              {home_team}
            </span>
            <div className="flex items-center gap-1.5 shrink-0">
              <span className={`text-sm font-bold tabular-nums ${homeWon ? 'text-emerald-600' : tied ? 'text-slate-600' : 'text-slate-400'}`}>
                {score_home}
              </span>
              <span className="text-xs text-slate-300">–</span>
              <span className={`text-sm font-bold tabular-nums ${awayWon ? 'text-emerald-600' : tied ? 'text-slate-600' : 'text-slate-400'}`}>
                {score_away}
              </span>
            </div>
            <span className={`text-sm font-medium truncate ${awayWon ? 'text-slate-900' : 'text-slate-500'}`}>
              {away_team}
            </span>
          </div>

          {/* Mobile layout — stacked */}
          <div className="flex md:hidden items-center gap-3 flex-1 min-w-0">
            <div className="flex flex-col min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {homeWon && <Trophy className="w-3 h-3 text-emerald-500 shrink-0" />}
                <span className={`text-sm font-medium truncate ${homeWon ? 'text-slate-900' : 'text-slate-500'}`}>
                  {home_team}
                </span>
                <span className={`ml-auto text-sm font-bold tabular-nums shrink-0 ${homeWon ? 'text-emerald-600' : 'text-slate-400'}`}>
                  {score_home}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {awayWon && <Trophy className="w-3 h-3 text-emerald-500 shrink-0" />}
                <span className={`text-sm font-medium truncate ${awayWon ? 'text-slate-900' : 'text-slate-500'}`}>
                  {away_team}
                </span>
                <span className={`ml-auto text-sm font-bold tabular-nums shrink-0 ${awayWon ? 'text-emerald-600' : 'text-slate-400'}`}>
                  {score_away}
                </span>
              </div>
            </div>
          </div>

          {needsMakeup && (
            <span className="flex items-center gap-1 text-[10px] font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full shrink-0">
              <AlertTriangle className="w-3 h-3" />
              <span className="hidden sm:inline">Makeup</span>
            </span>
          )}

          <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>

        {expanded && (
          <ScorecardDetail
            combinedMatchup={matchup}
            divisionNames={divisionNames}
            highlightPlayerIds={highlightPlayerIds}
          />
        )}
      </div>
    );
  }

  // Upcoming match
  return (
    <div className="flex items-center gap-2 px-3 py-2.5 md:py-2 rounded-lg">
      {/* Desktop */}
      <div className="hidden md:flex items-center gap-2 flex-1 min-w-0">
        <span className="text-sm font-medium text-slate-700 truncate">{home_team}</span>
        <span className="text-xs text-slate-400">vs</span>
        <span className="text-sm font-medium text-slate-700 truncate">{away_team}</span>
      </div>
      {/* Mobile */}
      <div className="flex md:hidden flex-col flex-1 min-w-0">
        <span className="text-sm font-medium text-slate-700 truncate">{home_team}</span>
        <span className="text-sm text-slate-400 truncate">vs {away_team}</span>
      </div>
      <span className="shrink-0 flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
        <Clock className="w-3 h-3" />
        <span className="hidden sm:inline">Upcoming</span>
      </span>
    </div>
  );
}

// ─── Week Card ──────────────────────────────────────────────────────

function WeekCard({ group, isCurrentWeek, divisionNames, expandOverride, matchupExpandOverride, highlightPlayerIds }: {
  group: WeekGroup;
  isCurrentWeek: boolean;
  divisionNames: Record<string, string>;
  expandOverride?: boolean;
  matchupExpandOverride?: boolean;
  highlightPlayerIds?: Set<string>;
}) {
  const [expanded, setExpanded] = useState(expandOverride ?? isCurrentWeek);
  const nonByeMatchups = group.matchups.filter(m => !m.is_bye);
  const byeMatchups = group.matchups.filter(m => m.is_bye);

  // Sync local state when controlled expansion changes
  useEffect(() => {
    setExpanded(expandOverride ?? isCurrentWeek);
  }, [expandOverride, isCurrentWeek]);

  return (
    <div
      className={`rounded-xl border transition-colors ${
        isCurrentWeek
          ? 'border-brand-200 bg-brand-50/30 ring-1 ring-brand-100'
          : 'border-slate-200 bg-white'
      }`}
    >
      {/* Week header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className={`text-sm font-semibold shrink-0 ${isCurrentWeek ? 'text-brand-700' : 'text-slate-700'}`}>
            Week {group.week}
          </span>
          <span className="text-sm text-slate-500 truncate">{formatWeekDate(group.date)}</span>
          {isCurrentWeek && (
            <span className="text-[10px] font-semibold uppercase tracking-wide text-brand-600 bg-brand-100 px-2 py-0.5 rounded-full shrink-0">
              Current
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-slate-400">
            {nonByeMatchups.length} {nonByeMatchups.length === 1 ? 'match' : 'matches'}
          </span>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          )}
        </div>
      </button>

      {/* Matchups */}
      {expanded && (
        <div className="px-2 pb-2 space-y-0.5">
          {nonByeMatchups.map((m, i) => (
            <MatchupRow
              key={`${m.home_team}-${m.away_team}-${i}`}
              matchup={m}
              divisionNames={divisionNames}
              expandOverride={m.is_played ? matchupExpandOverride : undefined}
              highlightPlayerIds={highlightPlayerIds}
            />
          ))}
          {byeMatchups.map((m, i) => (
            <MatchupRow key={`bye-${i}`} matchup={m} divisionNames={divisionNames} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Normalize team names for cross-division matching ───────────────

function normalizeTeamName(name: string): string {
  return name.trim().toUpperCase().replace(/\s+/g, ' ');
}

// ─── Compute standings per division from matchup data ────────────────

interface DivisionStandings {
  divisionId: string;
  label: string;
  standings: TeamStanding[];
}

function computePerDivisionStandings(
  weekGroups: WeekGroup[],
  divisionNames: Record<string, string>,
): DivisionStandings[] {
  // Collect all division IDs present in data
  const divisionIds = new Set<string>();
  for (const g of weekGroups) {
    for (const m of g.matchups) {
      for (const dm of m.divisionMatchups) divisionIds.add(dm.divisionId);
    }
  }

  const result: DivisionStandings[] = [];

  for (const divId of divisionIds) {
    const teams = new Map<string, TeamStanding>();

    const getTeam = (name: string): TeamStanding => {
      let t = teams.get(name);
      if (!t) {
        t = { team: name, wins: 0, losses: 0, ties: 0, pointsFor: 0, pointsAgainst: 0 };
        teams.set(name, t);
      }
      return t;
    };

    for (const g of weekGroups) {
      for (const cm of g.matchups) {
        if (cm.is_bye) continue;
        const dm = cm.divisionMatchups.find(d => d.divisionId === divId);
        if (!dm) continue;
        const m = dm.matchup;
        if (!m.is_played || m.score_home == null || m.score_away == null) continue;

        // Use the division-specific matchup's team names (should be same as combined)
        const home = getTeam(m.home_team);
        const away = getTeam(m.away_team);

        home.pointsFor += m.score_home;
        home.pointsAgainst += m.score_away;
        away.pointsFor += m.score_away;
        away.pointsAgainst += m.score_home;

        if (m.score_home > m.score_away) {
          home.wins++;
          away.losses++;
        } else if (m.score_away > m.score_home) {
          away.wins++;
          home.losses++;
        } else {
          home.ties++;
          away.ties++;
        }
      }
    }

    const standings = Array.from(teams.values()).sort((a, b) => {
      // NAPA ranks by total points (PF), then point differential
      if (b.pointsFor !== a.pointsFor) return b.pointsFor - a.pointsFor;
      return (b.pointsFor - b.pointsAgainst) - (a.pointsFor - a.pointsAgainst);
    });

    result.push({
      divisionId: divId,
      label: divisionNames[divId] || `Division ${divId}`,
      standings,
    });
  }

  return result;
}

// ─── Standings Table (reused per division) ──────────────────────────

function StandingsTable({
  standings,
  onTeamClick,
}: {
  standings: TeamStanding[];
  onTeamClick: (teamName: string) => void;
}) {
  return (
    <>
      {/* Desktop table */}
      <div className="hidden md:block">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-slate-400 border-b border-slate-100">
              <th className="text-left py-1.5 px-2 font-medium">#</th>
              <th className="text-left py-1.5 px-2 font-medium">Team</th>
              <th className="text-center py-1.5 px-2 font-medium">W</th>
              <th className="text-center py-1.5 px-2 font-medium">L</th>
              <th className="text-right py-1.5 px-2 font-medium">Pts Scored</th>
              <th className="text-right py-1.5 px-2 font-medium">Pts Allowed</th>
              <th className="text-right py-1.5 px-2 font-medium">+/–</th>
            </tr>
          </thead>
          <tbody>
            {standings.map((s, i) => {
              const diff = s.pointsFor - s.pointsAgainst;
              return (
                <tr
                  key={s.team}
                  onClick={() => onTeamClick(s.team)}
                  className="border-b border-slate-50 last:border-b-0 hover:bg-brand-50/60 cursor-pointer transition-colors"
                  title={`View ${s.team} player details`}
                >
                  <td className="py-1.5 px-2 text-slate-400 tabular-nums">{i + 1}</td>
                  <td className="py-1.5 px-2 font-medium text-slate-700 truncate max-w-[200px] hover:text-brand-700">{s.team}</td>
                  <td className="py-1.5 px-2 text-center tabular-nums font-semibold text-emerald-600">{s.wins}</td>
                  <td className="py-1.5 px-2 text-center tabular-nums text-slate-500">{s.losses}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums text-slate-600">{s.pointsFor}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums text-slate-400">{s.pointsAgainst}</td>
                  <td className={`py-1.5 px-2 text-right tabular-nums font-medium ${diff > 0 ? 'text-emerald-600' : diff < 0 ? 'text-red-400' : 'text-slate-400'}`}>
                    {diff > 0 ? '+' : ''}{diff}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile layout — compact cards */}
      <div className="md:hidden space-y-1">
        {standings.map((s, i) => {
          const diff = s.pointsFor - s.pointsAgainst;
          return (
            <button
              key={s.team}
              type="button"
              onClick={() => onTeamClick(s.team)}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-brand-50/60 active:bg-brand-100 transition-colors text-left"
            >
              <span className="text-xs text-slate-400 w-4 text-right shrink-0 tabular-nums">{i + 1}</span>
              <span className="text-sm font-medium text-slate-700 truncate flex-1">{s.team}</span>
              <span className="text-xs tabular-nums shrink-0">
                <span className="font-semibold text-emerald-600">{s.wins}</span>
                <span className="text-slate-300">-</span>
                <span className="text-slate-500">{s.losses}</span>
              </span>
              <span className={`text-xs tabular-nums shrink-0 w-10 text-right font-medium ${diff > 0 ? 'text-emerald-600' : diff < 0 ? 'text-red-400' : 'text-slate-400'}`}>
                {diff > 0 ? '+' : ''}{diff}
              </span>
            </button>
          );
        })}
      </div>
    </>
  );
}

// ─── Standings Card ─────────────────────────────────────────────────

function StandingsCard({
  divisionStandings,
  players,
}: {
  divisionStandings: DivisionStandings[];
  players?: DivisionPlayer[];
}) {
  const [expanded, setExpanded] = useState(false);
  const [teamDetail, setTeamDetail] = useState<{ team: string; label: string } | null>(null);

  const teamCount = divisionStandings[0]?.standings.length ?? 0;
  if (teamCount === 0) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <Trophy className="w-4 h-4 text-amber-500 shrink-0" />
          <span className="text-sm font-semibold text-slate-700">Team Standings</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-slate-400">{teamCount} teams</span>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="pb-2">
          {divisionStandings.map((ds) => (
            <div key={ds.divisionId}>
              {divisionStandings.length > 1 && (
                <div className="flex items-center gap-2 px-4 py-1.5 bg-slate-100/80 border-y border-slate-200">
                  <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">{ds.label}</span>
                </div>
              )}
              <div className="px-2">
                <StandingsTable
                  standings={ds.standings}
                  onTeamClick={(team) => setTeamDetail({ team, label: ds.label })}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <TeamDetailModal
        isOpen={teamDetail !== null}
        onClose={() => setTeamDetail(null)}
        teamName={teamDetail?.team ?? ''}
        divisionLabel={teamDetail?.label ?? ''}
        players={players ?? []}
      />
    </div>
  );
}

// ─── Main ScheduleView ─────────────────────────────────────────────

export default function ScheduleView({ divisionIds, divisions, players }: ScheduleViewProps) {
  const currentWeekRef = useRef<HTMLDivElement>(null);
  const [filterText, setFilterText] = useState('');
  const [expandLevel, setExpandLevel] = useState<0 | 1 | 2>(0);

  // Build divisionId → short label map
  const divisionNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const d of divisions) {
      map[d.division_id] = divisionLabel(d.name);
    }
    // Fallback for IDs not in divisions list
    for (const id of divisionIds) {
      if (!map[id]) map[id] = `Division ${id}`;
    }
    return map;
  }, [divisionIds, divisions]);

  // Fetch schedules for all division IDs in parallel
  const scheduleQueries = useQueries({
    queries: divisionIds.map(id => ({
      queryKey: queryKeys.schedule(id),
      queryFn: () => apiService.getSchedule(id),
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
    })),
  });

  const isLoading = scheduleQueries.some(q => q.isLoading);
  const error = scheduleQueries.find(q => q.error)?.error;

  // Merge schedules: combine matchups by week + team pairing, sum scores
  const { weekGroups, leagueName } = useMemo(() => {
    const allSchedules = scheduleQueries
      .map((q, i) => q.data ? { divisionId: divisionIds[i], schedule: q.data } : null)
      .filter(Boolean) as { divisionId: string; schedule: { league_name: string; matchups: ScheduleMatchup[]; current_week: number | null } }[];

    if (allSchedules.length === 0) return { weekGroups: [], leagueName: '' };

    const leagueName = allSchedules[0].schedule.league_name;

    // Index: week → normalizedHomeTeam|normalizedAwayTeam → CombinedMatchup
    const combined = new Map<number, Map<string, CombinedMatchup>>();

    for (const { divisionId, schedule } of allSchedules) {
      for (const m of schedule.matchups) {
        if (!combined.has(m.week)) combined.set(m.week, new Map());
        const weekMap = combined.get(m.week)!;

        // Key: sorted normalized team names for consistent matching
        const normHome = normalizeTeamName(m.home_team);
        const normAway = normalizeTeamName(m.away_team);
        const key = m.is_bye
          ? `BYE|${normHome || normAway}`
          : [normHome, normAway].sort().join('|');

        const existing = weekMap.get(key);
        if (existing) {
          // Add this division's scores to the existing combined matchup
          if (m.score_home != null && m.score_away != null) {
            existing.score_home = (existing.score_home ?? 0) + m.score_home;
            existing.score_away = (existing.score_away ?? 0) + m.score_away;
          }
          existing.is_played = existing.is_played || m.is_played;
          existing.divisionMatchups.push({ divisionId, matchup: m });
        } else {
          weekMap.set(key, {
            week: m.week,
            date: m.date,
            home_team: m.home_team,
            away_team: m.away_team,
            score_home: m.score_home,
            score_away: m.score_away,
            is_bye: m.is_bye,
            is_played: m.is_played,
            divisionMatchups: [{ divisionId, matchup: m }],
          });
        }
      }
    }

    const weekGroups: WeekGroup[] = [];
    for (const [week, weekMap] of combined) {
      const matchups = Array.from(weekMap.values());
      const date = matchups[0]?.date ?? '';
      weekGroups.push({ week, date, matchups });
    }
    weekGroups.sort((a, b) => a.week - b.week);

    return { weekGroups, leagueName };
  }, [scheduleQueries, divisionIds]);

  // Determine current week
  const currentWeek = useMemo(() => {
    for (const q of scheduleQueries) {
      if (q.data?.current_week) return q.data.current_week;
    }
    const playedWeeks = weekGroups
      .filter(g => g.matchups.some(m => m.is_played))
      .map(g => g.week);
    return playedWeeks.length > 0 ? Math.max(...playedWeeks) : null;
  }, [scheduleQueries, weekGroups]);

  // Compute per-division team standings
  const divisionStandings = useMemo(
    () => computePerDivisionStandings(weekGroups, divisionNames),
    [weekGroups, divisionNames],
  );

  // ─── Filter Logic ──────────────────────────────────────────────────
  // Build lookup: all team names from schedule data
  const allTeamNames = useMemo(() => {
    const names = new Set<string>();
    for (const g of weekGroups) {
      for (const m of g.matchups) {
        if (m.home_team) names.add(m.home_team);
        if (m.away_team) names.add(m.away_team);
      }
    }
    return names;
  }, [weekGroups]);

  // Build lookup: player name (lowercase) → { player_id, team_name }
  const playerLookup = useMemo(() => {
    const map = new Map<string, { player_id: string; team_name: string }[]>();
    if (!players) return map;
    for (const p of players) {
      if (p.name && p.team_name) {
        const key = p.name.toLowerCase();
        const existing = map.get(key) ?? [];
        existing.push({ player_id: p.player_id, team_name: p.team_name });
        map.set(key, existing);
      }
    }
    return map;
  }, [players]);

  // Compute filtered week groups, matched player IDs, and filter mode
  const filterResult = useMemo(() => {
    const empty = { groups: weekGroups, matchedPlayerIds: new Set<string>(), filterMode: null as 'team' | 'player' | null };
    const query = filterText.trim().toLowerCase();
    if (!query) return empty;

    // Find matching teams (substring)
    const matchingTeams = new Set<string>();
    for (const name of allTeamNames) {
      if (name.toLowerCase().includes(query)) matchingTeams.add(name);
    }

    // Find matching players and their teams
    const matchedPlayerIds = new Set<string>();
    const playerTeams = new Set<string>();
    for (const [normalizedName, entries] of playerLookup) {
      if (normalizedName.includes(query)) {
        for (const e of entries) {
          matchedPlayerIds.add(e.player_id);
          playerTeams.add(e.team_name);
        }
      }
    }

    const filterMode: 'team' | 'player' | null =
      matchedPlayerIds.size > 0 ? 'player' :
      matchingTeams.size > 0 ? 'team' : null;

    const relevantTeams = new Set([...matchingTeams, ...playerTeams]);
    if (relevantTeams.size === 0) {
      return { groups: [], matchedPlayerIds, filterMode: null };
    }

    const filtered = weekGroups.map(g => ({
      ...g,
      matchups: g.matchups.filter(m =>
        m.is_bye
          ? relevantTeams.has(m.home_team) || relevantTeams.has(m.away_team)
          : relevantTeams.has(m.home_team) || relevantTeams.has(m.away_team)
      ),
    })).filter(g => g.matchups.length > 0);

    return { groups: filtered, matchedPlayerIds, filterMode };
  }, [filterText, weekGroups, allTeamNames, playerLookup]);

  const isFilterActive = filterResult.filterMode !== null;

  // Compute controlled expansion overrides
  const weekExpandOverride = useMemo<boolean | undefined>(() => {
    if (isFilterActive) return true;
    if (expandLevel >= 1) return true;
    return undefined;
  }, [isFilterActive, expandLevel]);

  const matchupExpandOverride = useMemo<boolean | undefined>(() => {
    if (filterResult.filterMode === 'player') return true;
    if (expandLevel >= 2) return true;
    return undefined;
  }, [filterResult.filterMode, expandLevel]);

  const cycleExpandLevel = () => {
    setExpandLevel(prev => ((prev + 1) % 3) as 0 | 1 | 2);
  };

  const expandToggleIcon = expandLevel === 0
    ? <UnfoldVertical className="w-4 h-4" />
    : expandLevel === 1
    ? <ChevronsDown className="w-4 h-4" />
    : <FoldVertical className="w-4 h-4" />;

  const expandToggleLabel = expandLevel === 0
    ? 'Expand weeks'
    : expandLevel === 1
    ? 'Show details'
    : 'Collapse all';

  // Auto-scroll to current week on first render
  useEffect(() => {
    if (currentWeekRef.current) {
      currentWeekRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentWeek, weekGroups.length]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner size="md" message="Loading schedule..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <p className="text-sm text-red-600 mb-2">Failed to load schedule.</p>
          <p className="text-xs text-slate-400">{(error as Error).message}</p>
        </div>
      </div>
    );
  }

  if (!weekGroups.length) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-sm text-slate-400">No schedule data available for this division.</p>
      </div>
    );
  }

  // Summary stats
  const weeksPlayed = weekGroups.filter(g => g.matchups.some(m => m.is_played && !m.is_bye)).length;
  const totalWeeks = weekGroups.length;
  const matchesPlayed = weekGroups.reduce((acc, g) => acc + g.matchups.filter(m => m.is_played && !m.is_bye).length, 0);
  const matchesRemaining = weekGroups.reduce((acc, g) => acc + g.matchups.filter(m => !m.is_played && !m.is_bye).length, 0);

  // Count makeups needed: matchups where any division has < 3 individual matches
  // We approximate from schedule data: a matchup needs makeup if it has fewer divisions than expected
  // (actual makeup detection happens in ScorecardDetail, but we can flag from missing division matchups)
  const makeupsNeeded = weekGroups.reduce((acc, g) => {
    for (const m of g.matchups) {
      if (m.is_bye || !m.is_played) continue;
      // A matchup needs makeup if it doesn't have data from all divisions
      const divCount = m.divisionMatchups.filter(dm => dm.matchup.is_played).length;
      const expectedDivs = divisionIds.length;
      if (divCount < expectedDivs) acc++;
    }
    return acc;
  }, 0);

  const displayGroups = filterResult.groups;

  return (
    <div className="space-y-3">
      {/* Season summary strip */}
      {leagueName && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 px-1">
          <span className="font-medium text-slate-700">{leagueName}</span>
          <span>{weeksPlayed} of {totalWeeks} weeks played</span>
          <span>
            {matchesPlayed} matches played{makeupsNeeded > 0 && (
              <span className="text-amber-600 ml-1">({makeupsNeeded} {makeupsNeeded === 1 ? 'makeup' : 'makeups'} needed)</span>
            )}
          </span>
          <span>{matchesRemaining} remaining</span>
        </div>
      )}

      {/* Filter & expand toolbar */}
      <div className="flex items-center gap-2 px-1">
        <div className="relative flex-1 md:flex-none">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            className="w-full md:w-64 pl-9 pr-8 py-2 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 transition-all"
            placeholder="Filter by team or player..."
            value={filterText}
            onChange={(e) => { setFilterText(e.target.value); }}
          />
          {filterText && (
            <button
              onClick={() => setFilterText('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <button
          onClick={cycleExpandLevel}
          disabled={isFilterActive}
          title={isFilterActive ? 'Disabled while filter is active' : expandToggleLabel}
          className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-all shrink-0 ${
            isFilterActive
              ? 'border-slate-100 text-slate-300 cursor-not-allowed'
              : 'border-slate-200 bg-white hover:bg-slate-50 active:bg-slate-100 text-slate-600 hover:text-slate-800'
          }`}
        >
          {expandToggleIcon}
          <span className="hidden sm:inline text-xs">{expandToggleLabel}</span>
        </button>
      </div>

      {/* Team standings */}
      <StandingsCard divisionStandings={divisionStandings} players={players} />

      {/* Filter status */}
      {isFilterActive && (
        <div className="flex items-center gap-2 px-1 text-xs text-slate-500">
          <span>
            Showing {displayGroups.reduce((n, g) => n + g.matchups.filter(m => !m.is_bye).length, 0)} matchups
            {filterResult.filterMode === 'player' && ` for ${filterResult.matchedPlayerIds.size} player${filterResult.matchedPlayerIds.size !== 1 ? 's' : ''}`}
            {filterResult.filterMode === 'team' && ' matching team filter'}
          </span>
          <button onClick={() => setFilterText('')} className="text-brand-600 hover:text-brand-700 font-medium">Clear</button>
        </div>
      )}

      {filterText.trim() && displayGroups.length === 0 && (
        <div className="flex items-center justify-center py-12">
          <p className="text-sm text-slate-400">No matches found for "{filterText.trim()}"</p>
        </div>
      )}

      {/* Week cards */}
      {displayGroups.map((group) => (
        <div
          key={group.week}
          ref={group.week === currentWeek ? currentWeekRef : undefined}
        >
          <WeekCard
            group={group}
            isCurrentWeek={group.week === currentWeek}
            divisionNames={divisionNames}
            expandOverride={weekExpandOverride}
            matchupExpandOverride={matchupExpandOverride}
            highlightPlayerIds={filterResult.filterMode === 'player' ? filterResult.matchedPlayerIds : undefined}
          />
        </div>
      ))}
    </div>
  );
}
